// Global Instructions Rule Applied!
// Field configs of the Investor form, one function per FormSection. Keys are
// the view__investor_detail keys; the form holds a draft and saves explicitly
// (Create / Save), so nothing here auto-patches.

import { FieldConfig, FieldTypeEnum, SelectTypeEnum } from '../../../core/components/InputManager/types.ts'
import { SelectListEntry } from '../../../state_manager/data_models/select_list_entry.ts'
import { InvestorDetailFields } from '../../../state_manager/data_models/investor.ts'

/** What the form edits. The account is a company-select value while the
 *  investor is being created and fixed afterwards. */
export interface InvestorDraft {
  investors__name: string | null
  investors__legal_name: string | null
  investors__investor_number: string | null
  accounts__id: { value: string; label: string } | null
  investor_classifications__id: number | null
  legal_entity_types__id: number | null
  sbic_investor_types__id: number | null
  prog__notice_supplement_template_ids: number[]
}

export const EMPTY_INVESTOR_DRAFT: InvestorDraft = {
  investors__name: null,
  investors__legal_name: null,
  investors__investor_number: null,
  accounts__id: null,
  investor_classifications__id: null,
  legal_entity_types__id: null,
  sbic_investor_types__id: null,
  prog__notice_supplement_template_ids: []
}

export interface InvestorFieldOptions {
  classificationOptions: SelectListEntry[]
  legalEntityTypeOptions: SelectListEntry[]
  sbicInvestorTypeOptions: SelectListEntry[]
  noticeSupplementOptions: SelectListEntry[]
}

export const SBIC_INVESTOR_TYPE_HELP =
  'SBA capital treatment for Regulatory Capital (13 CFR 107.230): institutional commitments count in full, ' +
  'non-institutional investors count paid-in capital only, State/local government money is capped at 33% in ' +
  'aggregate. Applies to this investor in every fund. Not set = counted as non-institutional (paid-in only) on ' +
  'SBIC funds.'

export const NOTICE_DOCUMENTS_HELP =
  'Sent with every capital call notice for this investor, in addition to the notice itself. ' +
  'A commitment can use its own setting.'

function textField(key: string, label: string, value: string | null, extra: Partial<FieldConfig> = {}): FieldConfig {
  return { key, label, type: FieldTypeEnum.TEXT, value: value ?? null, editable: true, ...extra } as FieldConfig
}

function selectField(key: string, label: string, value: number | null, options: SelectListEntry[]): FieldConfig {
  return {
    key,
    label,
    type: FieldTypeEnum.SELECT,
    value: value ?? null,
    options,
    selectType: SelectTypeEnum.FILTER,
    allowClear: true,
    showSearch: true,
    editable: true
  }
}

/**
 * Investor section. The account is chosen while the investor is being
 * created (locked when the form was opened for one account). Afterwards it
 * is not a field of this list: see getAccountLinkFields.
 */
export function getInvestorFields(
  draft: InvestorDraft,
  { isNew, accountLocked }: { isNew: boolean; accountLocked: boolean }
): FieldConfig[] {
  const fields: FieldConfig[] = [
    textField(InvestorDetailFields.investors__name, 'Investor name', draft.investors__name, { required: true }),
    textField(InvestorDetailFields.investors__legal_name, 'Legal name', draft.investors__legal_name),
    textField(InvestorDetailFields.investors__investor_number, 'Investor number', draft.investors__investor_number)
  ]
  if (isNew) {
    fields.push({
      key: InvestorDetailFields.accounts__id,
      label: 'Account',
      type: FieldTypeEnum.COMPANY,
      value: draft.accounts__id,
      mode: 'single',
      required: true,
      editable: true,
      disabled: accountLocked
    })
  }
  return fields
}

/**
 * The account of an existing investor: it cannot be changed, so it is shown
 * as the record link to the account form. Rendered by a read-mode
 * InputManager — in edit mode every field is an input, read-only ones too.
 */
export function getAccountLinkFields(draft: InvestorDraft): FieldConfig[] {
  return [
    {
      key: InvestorDetailFields.accounts__id,
      label: 'Account',
      type: FieldTypeEnum.COMPANY,
      value: draft.accounts__id,
      mode: 'single',
      editable: false,
      hyperlinked: true,
      hyperlinkModule: 'companies'
    }
  ]
}

export function getClassificationFields(draft: InvestorDraft, options: InvestorFieldOptions): FieldConfig[] {
  return [
    selectField(
      InvestorDetailFields.investor_classifications__id,
      'Classification',
      draft.investor_classifications__id,
      options.classificationOptions
    ),
    selectField(
      InvestorDetailFields.legal_entity_types__id,
      'Legal entity type',
      draft.legal_entity_types__id,
      options.legalEntityTypeOptions
    ),
    {
      ...selectField(
        InvestorDetailFields.sbic_investor_types__id,
        'SBIC investor type',
        draft.sbic_investor_types__id,
        options.sbicInvestorTypeOptions
      ),
      labelTooltip: SBIC_INVESTOR_TYPE_HELP
    }
  ]
}

export function getNoticeFields(draft: InvestorDraft, options: InvestorFieldOptions): FieldConfig[] {
  return [
    {
      key: InvestorDetailFields.prog__notice_supplement_template_ids,
      label: 'Additional notice documents',
      type: FieldTypeEnum.SELECT,
      value: draft.prog__notice_supplement_template_ids,
      options: options.noticeSupplementOptions,
      selectType: SelectTypeEnum.FILTER,
      mode: 'multiple',
      allowClear: true,
      editable: true,
      placeholder: 'None',
      labelTooltip: NOTICE_DOCUMENTS_HELP
    }
  ]
}
